/**
 * Gather-node store — server `state.gatherNodes` mirror for client UI.
 *
 * Schema fields replicated (see packages/aiwari-server/src/schema.ts
 * GatherNodeState):
 *   - id, kind ('copper-vein' | 'silverleaf-bush' | …)
 *   - professionId ('mining' | 'herbalism' | …)
 *   - minSkill, yellow, green, grey
 *   - pos {x,y,z}
 *   - depletedAt, respawnSecs   → derive `available` flag client-side
 *   - zoneId
 *
 * The Babylon mesh installer (scene/GatherNodeMeshes.ts) and the click
 * handler in World.tsx subscribe here. Click → sendGatherNode(id) →
 * server validates skill+proximity → broadcasts depletion via state.
 */

import { create } from 'zustand';

export interface GatherNodeView {
  id: string;
  kind: string;
  professionId: 'mining' | 'herbalism' | 'skinning' | 'fishing' | string;
  minSkill: number;
  yellow: number;
  green: number;
  grey: number;
  pos: { x: number; y: number; z: number };
  depletedAt: number;
  respawnSecs: number;
  zoneId: string;
}

interface GatherStore {
  nodes: Record<string, GatherNodeView>;
  upsert: (n: GatherNodeView) => void;
  remove: (id: string) => void;
  clear: () => void;
  /** Compute available flag at a given epoch ms. */
  isAvailableAt: (id: string, nowMs: number) => boolean;
}

export const useGatherNodes = create<GatherStore>((set, get) => ({
  nodes: {},
  upsert: (n) => set((s) => ({ nodes: { ...s.nodes, [n.id]: n } })),
  remove: (id) =>
    set((s) => {
      const next = { ...s.nodes };
      delete next[id];
      return { nodes: next };
    }),
  clear: () => set({ nodes: {} }),
  isAvailableAt: (id, now) => {
    const n = get().nodes[id];
    if (!n) return false;
    if (n.depletedAt <= 0) return true;
    return now >= n.depletedAt + n.respawnSecs * 1000;
  },
}));
